import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import TCProductSelectStore, {
    CounterpartDevice,
    DeviceType,
    Grid,
    ProductSelectActions,
    RemoteDevice,
} from '@/components/productselect/tc.product.select.store';
import { useStore, waitForStore } from '@tk/utilities/tk.use.store';
import TCDeviceQuantityCounter from './tc.device.quantity.counter';

export default class TCCounterpartDevices extends TKCustomElementFactory {
    store?: TCProductSelectStore;
    protected unsubscribe?: () => void;

    counterpartDeviceAttribute = 'data-tc-device="counterpart"';
    articleNumberAttribute = 'data-tc-artno';
    imgAttribute = 'data-tc-device-img';
    titleAttribute = 'data-tc-device-title';
    subTitleAttribute = 'data-tc-device-subtitle';
    powerOutputAttribute = 'data-tc-device-power-output';
    quantityAttribute = 'data-tc-device-quantity';
    priceAttribute = 'data-tc-device-price';
    uvpAttribute = 'data-tc-device-price-uvp';
    deviceTypeAttribute = 'data-tc-device-type';
    cassetteAttribute = 'data-tc-cassette-device';
    remoteInclusiveAttribute = 'data-tc-remote-inclusive';
    remoteIdAttribute = 'data-tc-remote-id';
    remoteTitleAttribute = 'data-tc-remote-title';
    remoteSubTitleAttribute = 'data-tc-remote-subtitle';
    remotePriceAttribute = 'data-tc-remote-price';
    remoteUvpAttribute = 'data-tc-remote-price-uvp';
    remoteImgAttribute = 'data-tc-remote-img';
    remoteArtNoAttribute = 'data-tc-remote-artno';
    gridInclusiveAttribute = 'data-tc-grid-inclusive';
    gridIdAttribute = 'data-tc-grid-id';
    gridTitleAttribute = 'data-tc-grid-title';
    gridSubTitleAttribute = 'data-tc-grid-subtitle';
    gridPriceAttribute = 'data-tc-grid-price';
    gridUvpAttribute = 'data-tc-grid-price-uvp';
    gridImgAttribute = 'data-tc-grid-img';
    gridArtNoAttribute = 'data-tc-grid-artno';

    constructor() {
        super();
    }

    async connectedCallback() {
        await waitForStore('productSelectStore');
        this.store = useStore('productSelectStore') as TCProductSelectStore;
        this.initializeStore();
    }

    initializeStore() {
        if (!this.store) return;
        this.unsubscribe = this.store.subscribe(({ action }) => this.render(action as ProductSelectActions));
        this.sendInitialDataToStore();
    }

    sendInitialDataToStore() {
        const deviceElements = this.querySelectorAll(`[${this.counterpartDeviceAttribute}]`);
        if (deviceElements.length === 0) return;
        const devices: CounterpartDevice[] = [];

        deviceElements.forEach((element) => {
            const deviceType = element.getAttribute(this.deviceTypeAttribute) as DeviceType;
            const isCassette = element!.hasAttribute(this.cassetteAttribute);
            let remoteDevice: RemoteDevice | null = null;
            let grid: Grid | null = null;
            if (deviceType === DeviceType.INDOOR) {
                remoteDevice = {
                    parentId: element.id,
                    inclusive: element.getAttribute(this.remoteInclusiveAttribute) === '1',
                    isActive: true,
                    id: element.getAttribute(this.remoteIdAttribute) || '',
                    artNo: element.getAttribute(this.remoteArtNoAttribute) || '',
                    title: element.getAttribute(this.remoteTitleAttribute) || '',
                    subtitle: element.getAttribute(this.remoteSubTitleAttribute) || undefined,
                    price: element.getAttribute(this.remotePriceAttribute) || '',
                    uvpPrice: element.getAttribute(this.remoteUvpAttribute) || '',
                    image: element.getAttribute(this.remoteImgAttribute) || undefined,
                    deviceType: DeviceType.REMOTE,
                    hasSiblings: false,
                };
            }

            if (isCassette) {
                grid = {
                    parentId: element.id,
                    inclusive: element.getAttribute(this.gridInclusiveAttribute) === '1',
                    isActive: true,
                    id: element.getAttribute(this.gridIdAttribute) || '',
                    artNo: element.getAttribute(this.gridArtNoAttribute) || '',
                    title: element.getAttribute(this.gridTitleAttribute) || '',
                    subtitle: element.getAttribute(this.gridSubTitleAttribute) || undefined,
                    price: element.getAttribute(this.gridPriceAttribute) || '',
                    uvpPrice: element.getAttribute(this.gridUvpAttribute) || '',
                    image: element.getAttribute(this.gridImgAttribute) || undefined,
                    deviceType: DeviceType.GRID,
                    hasSiblings: false,
                };
            }

            const counterpartDevice: CounterpartDevice = {
                id: element.id,
                quantity: Number(element.getAttribute(this.quantityAttribute)),
                deviceType: deviceType,
                artNo: element.getAttribute(this.articleNumberAttribute) || '',
                title: element.getAttribute(this.titleAttribute) || '',
                subtitle: element.getAttribute(this.subTitleAttribute) || undefined,
                powerOutput: element.getAttribute(this.powerOutputAttribute) || '',
                price: element.getAttribute(this.priceAttribute) || '',
                uvpPrice: element.getAttribute(this.uvpAttribute) || '',
                image: element.getAttribute(this.imgAttribute) || undefined,
                remoteDevices: [],
                isActive: false,
                remotesInitalized: false,
                isCassette: isCassette,
                gridCollection: [],
                gridsInitalized: false,
            };
            remoteDevice && counterpartDevice.remoteDevices.push(remoteDevice);
            grid && counterpartDevice.gridCollection.push(grid);
            devices.push(counterpartDevice);
        });
        this.store?.setCounterpartDevices(devices);
    }

    static renderCounterpart(
        device: CounterpartDevice,
        template: HTMLTemplateElement,
        wrapper: HTMLElement,
        loginStatus: boolean,
    ) {
        const fragment = template.content.cloneNode(true) as DocumentFragment;
        const counterpartElement = fragment.firstElementChild as HTMLElement;
        if (!counterpartElement) return;

        counterpartElement.id = device.id;

        const img = counterpartElement.querySelector('[data-tc-device-img]') as HTMLImageElement;
        if (img && device.image) {
            img.src = device.image;
        }

        const title = counterpartElement.querySelector('[data-tc-device-title]');
        if (title) title.textContent = device.title;

        const subtitle = counterpartElement.querySelector('[data-tc-device-subtitle]');
        if (subtitle) subtitle.innerHTML = device.subtitle || '';

        if (device.deviceType === DeviceType.INDOOR) {
            const powerOutput = counterpartElement.querySelector('[data-tc-device-power-output]');
            powerOutput && (powerOutput.textContent = device.powerOutput);
        }

        const counter = counterpartElement.querySelector('[data-tc-device-quantity]') as HTMLInputElement;
        if (counter && device.quantity) counter.value = device.quantity?.toString();

        if (!loginStatus) wrapper.appendChild(counterpartElement);
        else {
            const price = counterpartElement.querySelector('[data-tc-device-price]');
            const priceContent = TCDeviceQuantityCounter.calculateTotalPrice(device.price, device.quantity || 1);
            if (priceContent !== '-') {
                price && (price.textContent = priceContent);
            } else {
                const priceWrapper = price?.closest('[data-tc-device-price-wrapper]') as HTMLElement;
                priceWrapper && priceWrapper.setAttribute('hidden', 'true');
            }

            wrapper.appendChild(counterpartElement);
        }
    }

    render(action: ProductSelectActions) {
        if (action !== ProductSelectActions.SET_PRICES) return;
        const devices = this.store!.getCounterparts();
        if (devices.length === 0) return;

        if (!this.store!.getLoginStatus()) return;
        const priceWrappers = this.querySelectorAll(`span[${this.priceAttribute}]`);
        priceWrappers.forEach((wrapper) => {
            const deviceElement = wrapper.closest(`[${this.counterpartDeviceAttribute}]`);
            const deviceId = deviceElement?.id;
            if (!deviceId) return;
            const device = devices.find((d) => d.id === deviceId);
            if (!device) return;
            const price = device.price;
            const priceContent = TCDeviceQuantityCounter.calculateTotalPrice(price, device.quantity || 1);
            wrapper.textContent = priceContent;
        });
    }
}
